/* eslint-disable no-promise-executor-return */
import { useCallback } from "react";
import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import whatsappApi from "./axiosWhatsApp";
import {
  WhatsAppMsg,
  ConvResumo,
  ContatoWA,
  WhatsAppMsgStatus,
  WhatsAppHistoryEntry,
  WhatsAppHistoryStatus,
  WhatsAppHistoryType,
  WhatsAppHistoryFilters,
  WhatsAppHistoryRecipient,
  SaldoEnvioEmMassa,
} from "./types";
import {
  buildWhatsAppSendFormData,
  buildWhatsAppBulkFormData,
  WhatsAppBulkPayload,
} from "./sendFormData";

const BASE_PATH = "whatsapp/common/whatsapp";

function normalizeMsgStatus(rawStatus: any): WhatsAppMsgStatus | undefined {
  if (rawStatus == null) return undefined;

  if (typeof rawStatus === "number") {
    if (rawStatus <= 0) return "PENDING";
    if (rawStatus === 1) return "SERVER_ACK";
    if (rawStatus === 2) return "DELIVERY_ACK";
    if (rawStatus >= 3) return "READ";
    return undefined;
  }

  const status = String(rawStatus).trim().toUpperCase();
  if (!status) return undefined;

  if (status === "PENDING") return "PENDING";
  if (status === "SERVER_ACK" || status === "SENT") return "SERVER_ACK";
  if (status === "DELIVERY_ACK" || status === "DELIVERED")
    return "DELIVERY_ACK";
  if (status === "READ") return "READ";
  if (status === "ERROR" || status === "FAILED") return "ERROR";

  return undefined;
}

function normalizeHistoryType(rawType: any): WhatsAppHistoryType {
  if (typeof rawType === "boolean") return rawType ? "CAMPANHA" : "INDIVIDUAL";

  const type = String(rawType ?? "")
    .trim()
    .toUpperCase();
  if (!type) return "INDIVIDUAL";

  if (
    type === "CAMPANHA" ||
    type === "BULK" ||
    type === "MASSA" ||
    type === "ENVIO EM MASSA"
  )
    return "CAMPANHA";

  return "INDIVIDUAL";
}

function normalizeHistoryStatus(rawStatus: any): WhatsAppHistoryStatus {
  // Prioriza leitura textual (o histórico manda PT: "Enviado", "Pendente",
  // "Erro", "Entregue", "Lida").
  const input = String(rawStatus ?? "")
    .trim()
    .toUpperCase();
  if (input === "LIDA" || input === "LIDO" || input === "READ") return "LIDA";
  if (input === "ENTREGUE" || input === "DELIVERED" || input === "DELIVERY_ACK")
    return "ENTREGUE";
  if (input === "ERRO" || input === "ERROR" || input === "FAILED")
    return "ERRO";
  if (input === "PENDENTE" || input === "PENDING") return "PENDENTE";
  if (input === "ENVIADO" || input === "ENVIADA" || input === "SENT")
    return "ENVIADA";

  // Fallback para status numérico/ack via normalizador de mensagem.
  const status = normalizeMsgStatus(rawStatus);
  if (status === "READ") return "LIDA";
  if (status === "DELIVERY_ACK") return "ENTREGUE";
  if (status === "ERROR") return "ERRO";
  if (status === "PENDING") return "PENDENTE";
  if (status === "SERVER_ACK") return "ENVIADA";

  return "ENVIADA";
}

function buildHistoryMessage(item: any): string {
  if (item?.mensagem) return String(item.mensagem);
  if (item?.conteudo) return String(item.conteudo);
  if (item?.texto) return String(item.texto);
  if (item?.descricao) return String(item.descricao);
  if (item?.nomeArquivo) return String(item.nomeArquivo);
  return "";
}

function parsePtBrDateTime(rawValue: any): {
  display: string;
  sort: string | undefined;
} {
  const value = String(rawValue ?? "").trim();
  if (!value) return { display: "", sort: undefined };

  const match = value.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (match) {
    const [, dd, mm, yyyy, hh = "00", min = "00", ss = "00"] = match;
    const iso = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    return { display: value, sort: iso };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { display: value, sort: undefined };
  }
  return { display: value, sort: parsed.toISOString() };
}

function toPositiveNumberOrZero(value: any): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed < 0 ? 0 : parsed;
}

function resolveHistoryStatus(
  item: any,
  qtdEnviado: number,
  qtdPendente: number,
  qtdErro: number
): WhatsAppHistoryStatus {
  const rawStatus =
    item?.status ?? item?.mensagemStatus ?? item?.statusMensagem ?? item?.ack;
  if (rawStatus != null && String(rawStatus).trim() !== "") {
    return normalizeHistoryStatus(rawStatus);
  }

  if (qtdErro > 0) return "ERRO";
  if (qtdPendente > 0) return "ENVIADA";
  if (qtdEnviado > 0) return "ENTREGUE";
  return "ENVIADA";
}

// Erro de um destinatário individual (campo descricaoErro no payload real).
function extractRecipientError(raw: any): string {
  const value =
    raw?.descricaoErro ??
    raw?.erro ??
    raw?.mensagemErro ??
    raw?.motivoErro ??
    raw?.errorMessage ??
    raw?.error ??
    "";
  return String(value || "").trim();
}

function mapHistoryRecipient(raw: any): WhatsAppHistoryRecipient {
  const nome = String(
    raw?.nome ?? raw?.leadNome ?? raw?.nomeContato ?? raw?.nomeLead ?? ""
  ).trim();
  const telefone = String(
    raw?.telefone ?? raw?.phone ?? raw?.numero ?? raw?.celular ?? ""
  ).trim();
  return {
    // Backend pode mandar nome/telefone null. Sem nome, usa o telefone;
    // sem ambos, fallback genérico (nunca expõe o id do lead).
    nome: nome || telefone || "Nome não encontrado",
    telefone,
    status: normalizeHistoryStatus(
      raw?.status ?? raw?.mensagemStatus ?? raw?.statusMensagem ?? raw?.ack
    ),
    erro: extractRecipientError(raw) || undefined,
  };
}

// O payload separa os contatos em três listas por status:
// leadsEnviados, leadsPendentes e leadsErro. Consolidamos numa só.
function extractHistoryRecipients(item: any): WhatsAppHistoryRecipient[] {
  const grupos = [
    item?.leadsEnviados,
    item?.leadsPendentes,
    item?.leadsErro,
    // Fallbacks para formatos alternativos.
    item?.destinatarios,
    item?.contatos,
    item?.leads,
  ];

  const recipients: WhatsAppHistoryRecipient[] = [];
  grupos.forEach((grupo) => {
    if (Array.isArray(grupo)) {
      grupo.forEach((raw) => recipients.push(mapHistoryRecipient(raw)));
    }
  });
  return recipients;
}

function mapHistoricoItem(item: any, index: number): WhatsAppHistoryEntry {
  const dataHoraInfo = parsePtBrDateTime(
    item?.dataHora ??
      item?.dataCriacao ??
      item?.dataEnvio ??
      item?.timestamp ??
      item?.createdAt
  );

  // Data de agendamento do disparo (quando o envio foi agendado p/ o futuro).
  const dataEnvioInfo = parsePtBrDateTime(
    item?.dataEnvio ??
      item?.dataAgendamento ??
      item?.dataAgendada ??
      item?.dataDisparo ??
      item?.dataProgramada
  );

  const tipo = normalizeHistoryType(
    item?.tipo ?? item?.tipoEnvio ?? item?.envioMassa ?? item?.isBulk
  );
  const arquivosUrl = Array.isArray(item?.arquivosUrl)
    ? item.arquivosUrl
        .map((url: any) => String(url || "").trim())
        .filter((url: string) => !!url)
    : [];

  const qtdTotal = toPositiveNumberOrZero(item?.qtdTotal);
  const qtdEnviado = toPositiveNumberOrZero(item?.qtdEnviado);
  const qtdPendente = toPositiveNumberOrZero(item?.qtdPendente);
  const qtdErro = toPositiveNumberOrZero(item?.qtdErro);

  const leadId = String(
    item?.leadId ??
      item?.idLeadCorretor365 ??
      item?.idLeadCorretor ??
      item?.leadCorretorId ??
      item?.rowKey ??
      item?.idContato ??
      ""
  );
  const leadNome = String(
    item?.leadNome ??
      item?.nomeLead ??
      item?.nomeContato ??
      item?.nome ??
      item?.telefone ??
      ""
  ).trim();

  const destinatarios = extractHistoryRecipients(item);

  // Não há mensagem de erro no nível do item; agregamos as descrições de
  // erro dos destinatários (distintas) num resumo. Se não houver descrição
  // mas existirem leads com erro, mostramos contagem.
  const errosDestinatarios = Array.from(
    new Set(
      destinatarios
        .filter((d) => d.status === "ERRO" && d.erro)
        .map((d) => d.erro as string)
    )
  );
  let erroResumo: string | undefined;
  if (errosDestinatarios.length > 0) {
    erroResumo = errosDestinatarios.join("\n");
  } else if (qtdErro > 0) {
    erroResumo = `${qtdErro} contato(s) com falha no envio.`;
  }

  return {
    id: String(
      item?.id ??
        item?.rowKey ??
        `${dataHoraInfo.sort || "wa-historico"}-${tipo}-${index}`
    ),
    leadId,
    leadNome: leadNome || (tipo === "CAMPANHA" ? "Envio em massa" : "-"),
    mensagem: buildHistoryMessage(item),
    tipo,
    status: resolveHistoryStatus(item, qtdEnviado, qtdPendente, qtdErro),
    dataHora: dataHoraInfo.display,
    dataHoraSort: dataHoraInfo.sort,
    dataEnvio: dataEnvioInfo.display || undefined,
    dataEnvioSort: dataEnvioInfo.sort,
    arquivosUrl,
    qtdTotal,
    qtdEnviado,
    qtdPendente,
    qtdErro,
    erro: erroResumo,
    destinatarios,
  };
}

export function useWhatsAppConversas(leadId: string) {
  return useQuery<WhatsAppMsg[]>({
    queryKey: ["wa-conv", leadId],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/conversa/v1/${leadId}`);
      // API retorna { success, data: { conversa: [...] } }
      const raw = response.data?.data;
      const conversa: any[] = raw?.conversa ?? [];

      // Invertendo a lista de conversas para que a mais recente seja a primeira
      return conversa
        .map((m: any, i: number) => ({
          // Garante id único — se a API retornar duas mensagens sem id/rowKey
          // ou com mesmo timestamp, fallback determinístico anexando o index
          // evita colisão de React keys (que faria mensagens "sumirem" na UI).
          id:
            m.id ||
            m.rowKey ||
            `msg-${i}-${m.dataCriacao || ""}-${m.direcao || ""}`,
          leadId,
          mensagem: m.mensagem || "",
          direcao: m.direcao as "saida" | "entrada",
          data: m.dataCriacao || "",
          mediaUrl: m.mediaUrl || undefined,
          mediaType: m.mediaType || undefined,
          status: normalizeMsgStatus(
            m.status ??
              m.mensagemStatus ??
              m.statusMensagem ??
              m.messageStatus ??
              m.ack
          ),
          editado: m.editado === true,
          dataHoraLimiteEdicao: m.dataHoraLimiteEdicao || undefined,
          dataHoraLimiteExclusao: m.dataHoraLimiteExclusao || undefined,
        }))
        .reverse();
    },
    enabled: !!leadId,
    refetchOnWindowFocus: false,
  });
}

export function useWhatsAppHistorico(
  enabled = true,
  filters: WhatsAppHistoryFilters = {}
) {
  const { status = null, dataInicial = null, dataFinal = null } = filters;

  return useQuery<WhatsAppHistoryEntry[]>({
    // Filtros entram na queryKey para refetch automático ao alterá-los.
    queryKey: ["wa-historico", status, dataInicial, dataFinal],
    queryFn: async () => {
      const response = await instance.post(`${BASE_PATH}/historico/v1`, {
        status,
        dataInicial,
        dataFinal,
      });
      const data = response.data?.data;
      let rows: any[] = [];
      if (Array.isArray(data)) {
        rows = data;
      } else if (Array.isArray(data?.historico)) {
        rows = data.historico;
      } else if (Array.isArray(data?.envios)) {
        rows = data.envios;
      } else if (Array.isArray(data?.itens)) {
        rows = data.itens;
      }

      return rows.map(mapHistoricoItem);
    },
    refetchOnWindowFocus: false,
    enabled,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao buscar histórico do WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

function addOptimistic(
  qc: ReturnType<typeof useQueryClient>,
  leadId: string,
  msg: Partial<WhatsAppMsg>
) {
  const now = new Date().toISOString();
  qc.setQueryData<WhatsAppMsg[]>(["wa-conv", leadId], (old) => [
    ...(old ?? []),
    {
      id: `tmp-${Date.now()}`,
      leadId,
      mensagem: "",
      direcao: "saida" as const,
      data: now,
      status: "PENDING" as const,
      ...msg,
    },
  ]);

  updateSentContactPreview(qc, leadId, msg, now);
}

function updateSentContactPreview(
  qc: ReturnType<typeof useQueryClient>,
  leadId: string,
  msg: Partial<WhatsAppMsg>,
  data = new Date().toISOString()
) {
  const mensagem = msg.mensagem || "";

  qc.setQueryData<ContatoWA[]>(["contatos-wa"], (old) => {
    if (!old) return old;
    return old.map((c) => {
      if (c.leadCorretorId !== leadId && c.rowKey !== leadId && c.id !== leadId)
        return c;
      return {
        ...c,
        ultimaMensagem: mensagem || c.ultimaMensagem,
        ultimaMensagemFromMe: true,
        ultimaAtividade: data,
        unreadCount: 0,
      };
    });
  });

  qc.setQueryData<ConvResumo[]>(["wa-conv-resumo"], (old) => {
    if (!old) return old;
    return old.map((resumo) =>
      resumo.leadId === leadId
        ? {
            ...resumo,
            ultimaMensagem: mensagem || resumo.ultimaMensagem,
            direcao: "saida" as const,
            data,
            naoLidas: 0,
          }
        : resumo
    );
  });
}

function refreshWhatsAppSidebarSoon(qc: ReturnType<typeof useQueryClient>) {
  window.setTimeout(() => {
    qc.invalidateQueries({ queryKey: ["contatos-wa"] });
    qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
  }, 1500);
}

function getApiErrorMessage(error: any, fallback: string): string {
  const notifications = error?.response?.data?.notifications;
  if (Array.isArray(notifications) && notifications.length > 0) {
    return notifications.map((item: any) => item?.message).join("\n");
  }
  return fallback;
}

function markLatestOptimisticError(
  qc: ReturnType<typeof useQueryClient>,
  leadId: string
) {
  qc.setQueryData<WhatsAppMsg[]>(["wa-conv", leadId], (old) => {
    if (!old || old.length === 0) return old;
    const index = [...old]
      .reverse()
      .findIndex((m) => m.id.startsWith("tmp-") && m.status === "PENDING");
    if (index < 0) return old;

    const target = old.length - 1 - index;
    return old.map((msg, idx) =>
      idx === target ? { ...msg, status: "ERROR" as const } : msg
    );
  });
}

export function useEnviarMensagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { leadId: string; mensagem: string }) => {
      const formData = buildWhatsAppSendFormData({
        idLeadCorretor365: body.leadId,
        mensagem: body.mensagem,
      });

      const response = await instance.post(`${BASE_PATH}/send/v1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onMutate: (body) =>
      addOptimistic(qc, body.leadId, {
        mensagem: body.mensagem,
        status: "PENDING",
      }),
    onSuccess: (_, v) => {
      updateSentContactPreview(qc, v.leadId, {
        mensagem: v.mensagem,
        status: "SERVER_ACK",
      });
      qc.invalidateQueries({ queryKey: ["wa-conv", v.leadId] });
      qc.invalidateQueries({ queryKey: ["wa-historico"] });
      refreshWhatsAppSidebarSoon(qc);
    },
    onError: (_, v) => {
      markLatestOptimisticError(qc, v.leadId);
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
      toast.error("Erro ao enviar mensagem");
    },
  });
}

export function useEditarMensagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      idMensagem,
      mensagem,
    }: {
      leadId: string;
      idMensagem: string;
      mensagem: string;
    }) => {
      const formData = new FormData();
      formData.append("IdMensagem", idMensagem);
      formData.append("Mensagem", mensagem);

      const response = await instance.put(`${BASE_PATH}/editar/v1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { data: response.data, leadId, idMensagem, mensagem };
    },
    onMutate: async ({ leadId, idMensagem, mensagem }) => {
      await qc.cancelQueries({ queryKey: ["wa-conv", leadId] });
      const previous =
        qc.getQueryData<WhatsAppMsg[]>(["wa-conv", leadId]) ?? [];

      qc.setQueryData<WhatsAppMsg[]>(["wa-conv", leadId], (old) =>
        (old ?? []).map((msg) =>
          msg.id === idMensagem ? { ...msg, mensagem } : msg
        )
      );

      return { previous, leadId };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["wa-conv", vars.leadId] });
      qc.invalidateQueries({ queryKey: ["wa-historico"] });
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
      toast.success("Mensagem editada.");
    },
    onError: (error: any, vars, context) => {
      if (context?.previous) {
        qc.setQueryData<WhatsAppMsg[]>(
          ["wa-conv", context.leadId],
          context.previous
        );
      }
      toast.error(getApiErrorMessage(error, "Erro ao editar mensagem"));
      qc.invalidateQueries({ queryKey: ["wa-conv", vars.leadId] });
    },
  });
}

export function useExcluirMensagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      idMensagem,
    }: {
      leadId: string;
      idMensagem: string;
    }) => {
      const response = await instance.delete(
        `${BASE_PATH}/excluir/v1/${idMensagem}`
      );
      return { data: response.data, leadId, idMensagem };
    },
    onMutate: async ({ leadId, idMensagem }) => {
      await qc.cancelQueries({ queryKey: ["wa-conv", leadId] });
      const previous =
        qc.getQueryData<WhatsAppMsg[]>(["wa-conv", leadId]) ?? [];

      qc.setQueryData<WhatsAppMsg[]>(["wa-conv", leadId], (old) =>
        (old ?? []).filter((msg) => msg.id !== idMensagem)
      );

      return { previous, leadId };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["wa-conv", vars.leadId] });
      qc.invalidateQueries({ queryKey: ["wa-historico"] });
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
      toast.success("Mensagem excluída.");
    },
    onError: (error: any, vars, context) => {
      if (context?.previous) {
        qc.setQueryData<WhatsAppMsg[]>(
          ["wa-conv", context.leadId],
          context.previous
        );
      }
      toast.error(getApiErrorMessage(error, "Erro ao excluir mensagem"));
      qc.invalidateQueries({ queryKey: ["wa-conv", vars.leadId] });
    },
  });
}

export function useEnviarArquivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, file }: { leadId: string; file: File }) => {
      const formData = buildWhatsAppSendFormData({
        idLeadCorretor365: leadId,
        arquivo: file,
        nomeArquivo: file.name,
      });

      const response = await instance.post(`${BASE_PATH}/send/v1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onMutate: ({ leadId, file }) => {
      const blobUrl = URL.createObjectURL(file);
      addOptimistic(qc, leadId, {
        mensagem: file.name,
        mediaType: file.type,
        mediaUrl: blobUrl,
        status: "PENDING",
      });
    },
    onSuccess: (_, v) => {
      updateSentContactPreview(qc, v.leadId, {
        mensagem: v.file.name,
        mediaType: v.file.type,
        status: "SERVER_ACK",
      });
      qc.invalidateQueries({ queryKey: ["wa-conv", v.leadId] });
      qc.invalidateQueries({ queryKey: ["wa-historico"] });
      refreshWhatsAppSidebarSoon(qc);
    },
    onError: (_, v) => {
      markLatestOptimisticError(qc, v.leadId);
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
      toast.error("Erro ao enviar arquivo");
    },
  });
}

const LS_KEY = (leadId: string) => `wa_conv_viewed_${leadId}`;

function normalizeContactMessageDate(value: any): string {
  const text = String(value || "").trim();
  if (!text) return "";

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(text)) {
    return text.replace(/Z$/, "");
  }

  return text;
}

export function marcarComoLido(leadId: string) {
  localStorage.setItem(LS_KEY(leadId), new Date().toISOString());
}

function resolveViewedAt(lastActivity?: string): string {
  const now = Date.now();
  const last = lastActivity ? new Date(lastActivity).getTime() : Number.NaN;
  if (!Number.isFinite(last)) return new Date(now).toISOString();

  return new Date(Math.max(now, last + 1000)).toISOString();
}

export function markWhatsAppContactRead(
  qc: QueryClient,
  leadId: string,
  lastActivity?: string
) {
  const viewedAt = resolveViewedAt(normalizeContactMessageDate(lastActivity));
  localStorage.setItem(LS_KEY(leadId), viewedAt);

  qc.setQueryData<ContatoWA[]>(["contatos-wa"], (old) =>
    old?.map((contato) => {
      const id = contato.leadCorretorId || contato.rowKey || contato.id || "";
      return id === leadId ? { ...contato, unreadCount: 0 } : contato;
    })
  );

  qc.setQueryData<ConvResumo[]>(["wa-conv-resumo"], (old) =>
    old?.map((resumo) =>
      resumo.leadId === leadId ? { ...resumo, naoLidas: 0 } : resumo
    )
  );
}

export function useWhatsAppConversasResumo() {
  return useQuery<ConvResumo[]>({
    queryKey: ["wa-conv-resumo"],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/contatos/v1`);
      const contatos: ContatoWA[] = response.data?.data?.contatos ?? [];

      return contatos.map((c) => {
        const leadId = c.id || c.leadCorretorId || c.rowKey || "";
        const data = normalizeContactMessageDate(
          c.ultimaAtividade ||
            (c as any).ultimaMensagemData ||
            (c as any).ultimaMensagemEm ||
            (c as any).dataUltimaMensagem ||
            (c as any).dataCriacaoUltimaMensagem ||
            ""
        );
        const viewedAt = localStorage.getItem(LS_KEY(leadId));
        const viewedAfterLast =
          viewedAt && data && new Date(data) <= new Date(viewedAt);

        return {
          leadId,
          ultimaMensagem: c.ultimaMensagem || "",
          direcao: c.ultimaMensagemFromMe
            ? ("saida" as const)
            : ("entrada" as const),
          data,
          naoLidas: viewedAfterLast ? 0 : c.unreadCount || 0,
        };
      });
    },
    refetchOnWindowFocus: false,
  });
}

export function useMarcarLido() {
  const qc = useQueryClient();
  return useCallback(
    (leadId: string, lastActivity?: string) => {
      markWhatsAppContactRead(qc, leadId, lastActivity);
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
    },
    [qc]
  );
}

/**
 * GET /whatsapp/common/whatsapp/bulk/saldo/v1
 * Consulta o saldo diário de envio em massa do corretor.
 */
export function useSaldoEnvioEmMassa(enabled = true) {
  return useQuery<SaldoEnvioEmMassa>({
    queryKey: ["wa-bulk-saldo"],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/bulk/saldo/v1`);
      const data = response.data?.data ?? response.data ?? {};
      const limite = Number(data?.limite ?? 0);
      const utilizado = Number(data?.utilizado ?? 0);
      const saldo = Number(data?.saldo ?? Math.max(limite - utilizado, 0));
      return { limite, utilizado, saldo };
    },
    enabled,
    refetchOnWindowFocus: false,
  });
}

/**
 * POST /whatsapp/common/whatsapp/bulk/v1
 * Envio em massa para múltiplos leads
 */
export function useEnvioEmMassa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: WhatsAppBulkPayload) => {
      const formData = buildWhatsAppBulkFormData(payload);
      const response = await instance.post(`${BASE_PATH}/bulk/v1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    mutationKey: ["whatsapp-bulk"],
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wa-historico"] });
      qc.invalidateQueries({ queryKey: ["wa-bulk-saldo"] });
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao enviar mensagens em massa";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

export function useEncaminharMensagens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      mensagens,
      destinatarios,
    }: {
      mensagens: WhatsAppMsg[];
      destinatarios: string[];
    }) => {
      const res = await whatsappApi.post("/whatsapp/encaminhar", {
        mensagemIds: mensagens.map((m) => m.id),
        destinatarios,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      vars.destinatarios.forEach((leadId) => {
        qc.invalidateQueries({ queryKey: ["wa-conv", leadId] });
      });
      qc.invalidateQueries({ queryKey: ["wa-conv-resumo"] });
    },
  });
}
